import { FileSearch, ServerCog, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";

const EASE = [0.25, 0.1, 0.25, 1] as const;

const CONTROL_CARDS = [
  {
    icon: FileSearch,
    title: "Traceable by design",
    body: "Every answer links back to the documents, checks, workflow version, and run that produced it.",
  },
  {
    icon: ShieldCheck,
    title: "Customer-controlled data",
    body: "Documents are processed for your workflows, not reused to train shared models.",
  },
  {
    icon: ServerCog,
    title: "Compliance-ready deployment",
    body: "Run with managed cloud, EU-region hosting, VPC, or dedicated infrastructure. DPAs, subprocessors, retention controls, access controls, and audit logs are part of the deployment model.",
  },
] as const;

const EnterpriseControlSection = () => {
  const shouldReduceMotion = useReducedMotion();

  const fadeUpInitial = shouldReduceMotion ? false : { opacity: 0, y: 18 };
  const transition = (delay = 0, duration = 0.7) => ({
    duration: shouldReduceMotion ? 0 : duration,
    ease: EASE,
    delay: shouldReduceMotion ? 0 : delay,
  });

  return (
    <section
      id="enterprise-control"
      aria-labelledby="enterprise-control-heading"
      className="border-y border-[oklch(0.88_0.008_75)] bg-white px-6 py-20 lg:px-16 lg:py-28"
    >
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-12 gap-y-8 lg:gap-x-6">
          <motion.div
            className="col-span-12 lg:col-span-5"
            initial={fadeUpInitial}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={transition()}
          >
            <p className="mb-4 text-xs font-medium uppercase tracking-[0.2em]" style={{ color: "oklch(var(--primary))" }}>
              ENTERPRISE CONTROL
            </p>
            <h2
              id="enterprise-control-heading"
              className="max-w-none font-serif text-[clamp(2rem,11vw,2.5rem)] font-semibold leading-[0.98] tracking-[-0.025em] text-foreground lg:text-[3.35rem]"
            >
              <span data-testid="enterprise-control-heading-line" className="block whitespace-nowrap">
                Governed from
              </span>
              <span data-testid="enterprise-control-heading-line" className="block whitespace-nowrap">
                source to answer.
              </span>
            </h2>
          </motion.div>

          <motion.div
            className="col-span-12 space-y-5 lg:col-span-5 lg:col-start-8"
            initial={fadeUpInitial}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={transition(0.08)}
          >
            <p className="text-lg font-light leading-relaxed text-foreground lg:text-xl">
              Lumient is designed for teams that need operational AI without surrendering control of their data,
              evidence, or infrastructure choices.
            </p>
            <p className="text-[15px] font-light leading-relaxed text-muted-foreground">
              Every Lumient record keeps its source evidence, workflow version, permissions, and audit history attached,
              so teams can inspect how an answer was produced and decide where the system is allowed to run.
            </p>
          </motion.div>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-4 lg:mt-16 lg:grid-cols-3 lg:gap-6">
          {CONTROL_CARDS.map((card, index) => {
            const Icon = card.icon;

            return (
              <motion.article
                key={card.title}
                data-testid="enterprise-control-card"
                className="rounded-2xl border border-[oklch(0.88_0.008_75)] bg-[oklch(0.995_0.002_75)] p-6 shadow-[0_14px_36px_oklch(0.2_0.01_75_/_0.035)] lg:p-7"
                initial={fadeUpInitial}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.18 }}
                transition={transition(0.16 + index * 0.1, 0.68)}
              >
                <div
                  data-testid="enterprise-control-card-icon"
                  aria-hidden="true"
                  className="flex h-10 w-10 items-center justify-center rounded-xl border border-[oklch(0.88_0.008_75)] bg-white text-primary shadow-[0_8px_20px_oklch(0.2_0.01_75_/_0.04)]"
                >
                  <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
                </div>
                <h3 className="mt-6 font-serif text-[1.65rem] font-semibold leading-tight text-foreground">
                  {card.title}
                </h3>
                <p className="mt-4 text-sm font-light leading-relaxed text-muted-foreground">{card.body}</p>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default EnterpriseControlSection;
